/**
 * @name stories
 *
 * @description
 * Image helpers shared by the Stories components. A story holds a single Cloudinary image plus
 * an optional mobile override: these helpers resolve the right asset per breakpoint and crop it
 * to the expected aspect ratio when no dedicated mobile asset is authored.
 */
import type {Transformation} from '_types'
import {updateCloudinaryPDImage} from './cloudinary'
import type {PageContentStoriesItemComponent} from './pageDesigner'

// Fallback media ratios (height / width * 100) used when the editor didn't author `media_ratio`.
export const DEFAULT_STORY_RATIO = '150' // 2:3 portrait (Figma: 253.45 x 379.18)
export const DEFAULT_BANNER_RATIO = '56' // 16:9 landscape
export const DEFAULT_BANNER_MOBILE_RATIO = '75' // 4:3 landscape

// Cloudinary chokes on decimals in `ar_`: turn height / width * 100 into a reduced integer pair.
const toCloudinaryAspectRatio = (ratio: string): string => {
    const height = Math.round(Number(ratio) * 100)
    const width = 10000
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
    const divisor = gcd(width, height) || 1
    return `${width / divisor}:${height / divisor}`
}

/**
 * @name addCloudinaryCrop
 *
 * @description
 * Inserts a chained Cloudinary transformation in the URL so the asset is cropped to the
 * requested aspect ratio. Uses `c_fill` with `g_auto` and an aspect-ratio qualifier: without
 * explicit width/height Cloudinary scales relative to the original dimensions and crops to
 * the ratio, while `g_auto` preserves the most visually important area.
 *
 * @see https://cloudinary.com/documentation/image_crop_modes
 * @see https://cloudinary.com/documentation/image_automatic_gravity
 *
 * @param {string} url - The Cloudinary image URL.
 * @param {string} ratio - Height / Width * 100, ex. '56.25' for 16:9.
 * @returns {string} The URL with the crop transformation applied.
 */
export const addCloudinaryCrop = (url: string, ratio: string): string => {
    if (!url) return url
    const cropSegment = `c_fill,g_auto,ar_${toCloudinaryAspectRatio(ratio)}`
    if (/\/v\d+\//.test(url)) {
        return url.replace(/(\/v\d+\/)/, `/${cropSegment}$1`)
    }
    return url.replace(/\/([^/]+)$/, `/${cropSegment}/$1`)
}

export interface StoryImageSources {
    desktop?: string
    mobile?: string
    alt: string
}

// Media ratios of a story, shared by the Cloudinary crop and the CSS aspect ratio so they always match.
const getStoryRatios = (story: PageContentStoriesItemComponent, isFullWidth: boolean) => ({
    desktop: story.mediaRatio ?? (isFullWidth ? DEFAULT_BANNER_RATIO : DEFAULT_STORY_RATIO),
    mobile: story.mediaRatioMobile ?? story.mediaRatio ?? (isFullWidth ? DEFAULT_BANNER_MOBILE_RATIO : DEFAULT_STORY_RATIO),
})

/**
 * @name resolveStoryImages
 *
 * @description
 * Resolves the Cloudinary URLs of a story's image for the desktop and mobile breakpoints. A
 * dedicated mobile asset is used as authored; when it's missing, the desktop asset is cropped
 * by Cloudinary to the mobile ratio.
 *
 * @param {PageContentStoriesItemComponent} story - The mapped story.
 * @param {Transformation} globalTransformation - Global Cloudinary image transformation from app preferences.
 * @param {string} [cloudinaryCNAME] - Cloudinary CNAME from app preferences.
 * @param {boolean} [isFullWidth] - Whether the card spans the whole grid row.
 * @returns {StoryImageSources} The per-breakpoint sources and the alt text.
 */
export const resolveStoryImages = (
    story: PageContentStoriesItemComponent,
    globalTransformation: Transformation,
    cloudinaryCNAME?: string,
    isFullWidth = false,
): StoryImageSources => {
    const ratios = getStoryRatios(story, isFullWidth)
    const desktop = story.image?.imageUrl ? updateCloudinaryPDImage(story.image, globalTransformation, cloudinaryCNAME) : undefined
    const mobileAsset = story.imageMobile

    return {
        desktop: desktop ? addCloudinaryCrop(desktop, ratios.desktop) : undefined,
        mobile: mobileAsset?.imageUrl
            ? updateCloudinaryPDImage(mobileAsset, globalTransformation, cloudinaryCNAME)
            : desktop && addCloudinaryCrop(desktop, ratios.mobile),
        alt: story.image?.alt || story.title || '',
    }
}

// Page Designer stores the ratio as height / width * 100; CSS `aspect-ratio` wants width / height.
const toAspectRatio = (ratio: string): number | undefined => {
    const value = Number(ratio)
    return Number.isFinite(value) && value > 0 ? 100 / value : undefined
}

/**
 * @name getStoryAspectRatio
 *
 * @description
 * Resolves the responsive CSS `aspect-ratio` of a story image. Editors can override it per story
 * through the `media_ratio` / `media_ratio_mobile` attributes; otherwise grid cards default to a
 * portrait ratio and full-width banners to a landscape one.
 *
 * @param {PageContentStoriesItemComponent} story - The mapped story.
 * @param {boolean} [isFullWidth] - Whether the card spans the whole grid row.
 * @returns {{base?: number, md?: number}} The per-breakpoint aspect ratio.
 */
export const getStoryAspectRatio = (story: PageContentStoriesItemComponent, isFullWidth = false) => {
    const ratios = getStoryRatios(story, isFullWidth)
    return {base: toAspectRatio(ratios.mobile), md: toAspectRatio(ratios.desktop)}
}
